import './Card.css';
import React, { CSSProperties } from 'react';
import classnames from 'classnames';
import GlobalProps from '../globalProps';
import { ScrollWrapper } from '../ScrollWrapper/ScrollWrapper';
import { Icon } from '../Icon/Icon';
import { Stack } from '../Stack/Stack';

export interface Props extends GlobalProps {
  anchorId?: string;
  title?: React.ReactNode | string;
  actions?: React.ReactNode;
  elevation?: number;
  footer?: React.ReactNode;
  pagination?: React.ReactNode;
  size?: 'xs' | 'small' | 'medium' | 'large' | 'dashboard';
  fullbleed?: boolean;
  scrollShadows?: boolean;
  scrollOverflow?: boolean;
  loading?: boolean;
  withSelectedItems?: boolean;
  tooltip?: () => React.ReactNode;
  height?: number;
}

export function Card({
  anchorId,
  title,
  actions,
  elevation,
  footer,
  id,
  className,
  style,
  children,
  pagination,
  size = 'small',
  fullbleed,
  scrollShadows,
  loading,
  withSelectedItems,
  tooltip,
  height,
}: Props): JSX.Element {
  function renderContent(): JSX.Element {
    if (loading) {
      return <Icon name="activity" loading={true} className="card__content card__content--loading" />;
    }
    if (scrollShadows) {
      return <ScrollWrapper className="card__content">{children}</ScrollWrapper>;
    }
    if (children) {
      return <div className="card__content">{children}</div>;
    }
    return <div />;
  }

  return (
    <div
      id={id}
      className={classnames(
        'card',
        `card--elevation-${elevation || 2}`,
        `card--${size}`,
        {
          'card--no-footer': !footer,
          'card--no-header': !title && !actions,
          'card--fullbleed': fullbleed,
          'card--loading': loading,
          'card--with-selected-items': withSelectedItems,
        },
        className
      )}
      style={{ ...style, '--card-custom-height': height ? `${height}px` : undefined } as CSSProperties}
    >
      {anchorId && <span id={anchorId} />}
      {(title || actions) && (
        <header className="card__header">
          <Stack size="xs" line>
            {title && <div className="card__title">{title}</div>}
            {tooltip?.()}
          </Stack>
          {actions && <div className="card__actions">{actions}</div>}
        </header>
      )}
      {renderContent()}
      {footer && <footer className="card__footer">{footer}</footer>}
      {pagination && <footer className="card__footer card__pagination">{pagination}</footer>}
    </div>
  );
}
